import { useState } from "react";
import { AmountInput, Button, Dot, Panel, Row, Segmented } from "../../components/ui";
import { stake, unstake } from "../../state/actions";
import { big, fromRaw, toRaw } from "../../lib/num";
import { papers, papersExact, usePaused, useStakeMinimum, type Me } from "./model";

type Mode = "stake" | "unstake";

type Verdict = { ok: true; amount: bigint; label: string } | { ok: false; reason: string };

const CHIPS = [
  { label: "25%", part: 25n },
  { label: "50%", part: 50n },
  { label: "Max", part: 100n },
] as const;

function judge(mode: Mode, me: Me, text: string, minimum: bigint | null, paused: boolean): Verdict {
  if (me.state === "disconnected") return { ok: false, reason: "Connect a wallet" };
  if (me.state === "loading") return { ok: false, reason: "Loading your account" };
  if (paused) return { ok: false, reason: mode === "stake" ? "Staking is paused" : "Unstaking is paused" };
  if (text.trim() === "") return { ok: false, reason: "Enter an amount" };
  const amount = toRaw(text, 18);
  if (amount === null) return { ok: false, reason: "Enter a valid amount" };
  if (amount === 0n) return { ok: false, reason: "Enter an amount" };
  if (mode === "stake") {
    if (minimum !== null && amount < minimum) return { ok: false, reason: `Minimum is ${papers(minimum)} PAPER` };
    if (amount > me.balance) return { ok: false, reason: "Not enough PAPER" };
    return { ok: true, amount, label: `Stake ${papersExact(amount)} PAPER` };
  }
  if (amount > me.staked) return { ok: false, reason: "More than you have staked" };
  return { ok: true, amount, label: `Unstake ${papersExact(amount)} PAPER` };
}

/** Stake or unstake: an exact amount, chips on the balance it draws from, the reason when it can't go. */
export function StakePanel({ me }: { me: Me }) {
  const [mode, setMode] = useState<Mode>("stake");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const minimum = useStakeMinimum();
  const paused = usePaused(mode === "stake" ? "paperStake" : "paperUnstake");

  const ready = me.state === "ready" ? me : null;
  const source = ready ? (mode === "stake" ? ready.balance : ready.staked) : null;
  const typed = text.trim() === "" ? null : toRaw(text, 18);
  const verdict = judge(mode, me, text, minimum, paused);
  const moved = verdict.ok ? verdict.amount : 0n;
  const after = ready ? (mode === "stake" ? ready.staked + moved : ready.staked - moved) : null;
  const pending = ready?.pending.filter((operation) => operation.action !== "paperClaim") ?? [];

  async function submit() {
    if (!verdict.ok || busy) return;
    setBusy(true);
    try {
      const amount = fromRaw(verdict.amount, 18);
      await (mode === "stake" ? stake(amount) : unstake(amount));
      setText("");
    } catch {
      // told by a toast
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Stake" className="stk-panel">
      <Segmented<Mode>
        label="Stake or unstake"
        value={mode}
        onChange={(next) => {
          setMode(next);
          setText("");
        }}
        options={[
          { value: "stake", label: "Stake" },
          { value: "unstake", label: "Unstake" },
        ]}
      />

      <div className="field">
        <div className="field-head">
          <span className="label">Amount</span>
          <span className="label">
            {mode === "stake" ? "In wallet" : "Staked"}{" "}
            <span className="num stk-avail">{source === null ? "—" : papers(source)}</span>
          </span>
        </div>
        <AmountInput
          label={mode === "stake" ? "PAPER to stake" : "PAPER to unstake"}
          value={text}
          onChange={setText}
          places={18}
          suffix="PAPER"
          onEnter={submit}
        />
        <div className="chips">
          {CHIPS.map((chip) => {
            const value = source === null ? null : (source * chip.part) / 100n;
            return (
              <button
                key={chip.label}
                type="button"
                className="chip"
                disabled={value === null || value === 0n}
                aria-pressed={value !== null && value > 0n && typed === value}
                onClick={() => value !== null && setText(fromRaw(value, 18))}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="stk-rows">
        {mode === "stake" && <Row label="Minimum stake">{minimum === null ? "—" : `${papers(minimum)} PAPER`}</Row>}
        <Row label="Staked after">{after === null ? "—" : `${papers(after)} PAPER`}</Row>
      </div>

      {pending.length > 0 && (
        <ul className="stk-pending" aria-label="Waiting for the next batch">
          {pending.map((operation) => (
            <li key={operation.intentId}>
              <Dot tone="warn" />
              <span>
                {operation.action === "paperStake" ? "Stake" : "Unstake"}{" "}
                <span className="num">{operation.amount ? papers(big(operation.amount)) : ""}</span> PAPER
              </span>
              <span className="faint">next batch</span>
            </li>
          ))}
        </ul>
      )}

      <div className="stk-push stack">
        <Button tone={verdict.ok ? "primary" : "secondary"} size="lg" block busy={busy} disabled={!verdict.ok} onClick={submit}>
          {verdict.ok ? verdict.label : verdict.reason}
        </Button>
        <p className="note">
          Your wallet signs on HyperEVM. No gas. Takes effect in the next batch.
          {mode === "unstake" && " What you have already earned stays claimable."}
        </p>
      </div>
    </Panel>
  );
}
